/**
 * Framework-neutral React Router navigation helpers for the dashboard SPA.
 */
import {
  useLocation,
  useNavigate,
  useSearchParams as useRrSearchParams,
  Navigate,
} from "react-router-dom";
import type { ReactElement } from "react";

export function usePathname(): string {
  return useLocation().pathname;
}

export function useRouter() {
  const navigate = useNavigate();
  return {
    push: (to: string) => {
      navigate(to);
    },
    replace: (to: string) => {
      navigate(to, { replace: true });
    },
    back: () => {
      navigate(-1);
    },
    refresh: () => {
      window.location.reload();
    },
    prefetch: async (_to: string) => {
      /* SPA has no separate prefetch layer */
    },
  };
}

/** Current location search params (reactive). */
export function useSearchParams(): URLSearchParams {
  const [params] = useRrSearchParams();
  return params;
}

/**
 * Declarative in-app redirect for route elements.
 * Prefer `<Navigate>` / this helper over imperative window redirects in route modules.
 */
export function Redirect({ to, replace = true }: { to: string; replace?: boolean }): ReactElement {
  return <Navigate to={to} replace={replace} />;
}
